import { supabase } from '@/integrations/supabase/client';
import type { 
  InventoryStock, 
  InventoryTransaction, 
  DailyStockCount, 
  DailyDamage,
  CreateInventoryTransactionInput,
  CreateDailyStockCountInput,
  CreateDailyDamageInput
} from '@/types/database/inventory';
import { CategoryType } from '@/constants/categoryTypes';

// ==================== Inventory Stock ====================

export async function getInventoryStock(filters?: {
  productId?: string;
  lowStockOnly?: boolean;
  categoryType?: CategoryType;
}): Promise<InventoryStock[]> {
  // We need to join through products to categories to filter by category_type
  let query = supabase
    .from('inventory_stock')
    .select(`
      *,
      product:products(
        id, 
        name, 
        category_id, 
        unit_of_measure,
        category:categories(id, name, category_type)
      )
    `)
    .order('last_updated', { ascending: false });

  if (filters?.productId) {
    query = query.eq('product_id', filters.productId);
  }

  const { data, error } = await query;
  
  if (error) throw error;
  
  let results = data as unknown as InventoryStock[];
  
  // Filter by category type client-side (since we need to join through products -> categories)
  if (filters?.categoryType) {
    results = results.filter((item) => {
      const product = item.product as any;
      return product?.category?.category_type === filters.categoryType;
    });
  }
  
  return results;
}

export async function getLowStockProducts(): Promise<{
  product_id: string;
  product_name: string;
  current_quantity: number;
  reorder_level: number;
  unit_of_measure: string;
}[]> {
  const { data, error } = await supabase.rpc('get_low_stock_products');
  
  if (error) throw error;
  return data || [];
}

export async function getStockLevel(productId: string): Promise<number> {
  const { data, error } = await supabase.rpc('get_stock_level', {
    p_product_id: productId
  });
  
  if (error) throw error;
  return data || 0;
}

export async function updateReorderLevel(
  productId: string, 
  reorderLevel: number
): Promise<void> {
  const { error } = await supabase
    .from('inventory_stock')
    .update({ reorder_level: reorderLevel })
    .eq('product_id', productId);
  
  if (error) throw error;
}

// ==================== Inventory Transactions ====================

export async function getInventoryTransactions(filters?: {
  productId?: string;
  transactionType?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
}): Promise<InventoryTransaction[]> {
  let query = supabase
    .from('inventory_transactions')
    .select(`
      *,
      product:products(id, name, unit_of_measure)
    `)
    .order('created_at', { ascending: false });

  if (filters?.productId) {
    query = query.eq('product_id', filters.productId);
  }

  if (filters?.transactionType) {
    query = query.eq('transaction_type', filters.transactionType);
  }

  if (filters?.startDate) {
    query = query.gte('created_at', filters.startDate);
  }

  if (filters?.endDate) {
    query = query.lte('created_at', filters.endDate);
  }

  if (filters?.limit) {
    query = query.limit(filters.limit);
  }

  const { data, error } = await query;
  
  if (error) throw error;
  return data as unknown as InventoryTransaction[];
}

export async function recordInventoryTransaction(
  input: CreateInventoryTransactionInput
): Promise<InventoryTransaction> {
  const { data: userData } = await supabase.auth.getUser();
  
  const { data, error } = await supabase.rpc('record_inventory_transaction', {
    p_product_id: input.product_id,
    p_transaction_type: input.transaction_type,
    p_quantity: input.quantity,
    p_unit_cost: input.unit_cost || null,
    p_reference_type: input.reference_type,
    p_reference_id: input.reference_id || null,
    p_reference_number: input.reference_number || null,
    p_notes: input.notes || null,
    p_created_by: userData?.user?.id || null
  });
  
  if (error) throw error;
  return data as unknown as InventoryTransaction;
}

// ==================== Batch Layer Functions ====================

export interface CreateBatchLayerInput {
  product_id: string;
  quantity: number;
  unit_cost: number;
  procurement_id?: string;
  procurement_item_id?: string;
  received_date?: string;
}

/**
 * Records an inbound inventory transaction AND creates a batch layer for FIFO tracking.
 * Use this for procurement warehouse confirmation instead of recordInventoryTransaction.
 */
export async function recordInboundWithBatchLayer(
  input: CreateBatchLayerInput & {
    reference_number?: string;
    notes?: string;
  }
): Promise<{ transaction: InventoryTransaction; batchLayerId: string }> {
  const { data: userData } = await supabase.auth.getUser();
  
  // 1. Record the inventory transaction
  const { data: transactionData, error: transactionError } = await supabase.rpc('record_inventory_transaction', {
    p_product_id: input.product_id,
    p_transaction_type: 'inbound',
    p_quantity: input.quantity,
    p_unit_cost: input.unit_cost,
    p_reference_type: 'procurement',
    p_reference_id: input.procurement_item_id || null,
    p_reference_number: input.reference_number || null,
    p_notes: input.notes || null,
    p_created_by: userData?.user?.id || null
  });
  
  if (transactionError) throw transactionError;
  
  const transaction = transactionData as unknown as InventoryTransaction;
  
  // 2. Create the batch layer for FIFO tracking
  const { data: batchLayer, error: batchError } = await supabase
    .from('inventory_batch_layers')
    .insert({
      product_id: input.product_id,
      inbound_transaction_id: transaction.id,
      procurement_item_id: input.procurement_item_id || null,
      procurement_id: input.procurement_id || null,
      initial_quantity: input.quantity,
      remaining_quantity: input.quantity,
      unit_cost: input.unit_cost,
      received_date: input.received_date || new Date().toISOString()
    })
    .select('id')
    .single();
  
  if (batchError) {
    console.error('[BATCH LAYER] Failed to create batch layer:', batchError);
    // Don't throw - transaction was recorded, batch layer is for enhanced tracking
    return { transaction, batchLayerId: '' };
  }
  
  console.log(`[BATCH LAYER] Created batch layer ${batchLayer.id} for product ${input.product_id}: ${input.quantity}kg @ ${input.unit_cost}`);
  
  return { transaction, batchLayerId: batchLayer.id };
}

/**
 * Get batch layer status for a product
 */
export async function getBatchLayerStatus(productId?: string): Promise<{
  batch_layer_id: string;
  product_id: string;
  product_name: string;
  procurement_id: string | null;
  initial_quantity: number;
  remaining_quantity: number;
  consumed_quantity: number;
  unit_cost: number;
  total_remaining_value: number;
  received_date: string;
  age_days: number;
}[]> {
  const { data, error } = await supabase.rpc('get_batch_layer_status', {
    p_product_id: productId || null
  });
  
  if (error) throw error;
  return data || [];
}

/**
 * Consume from batch layers using FIFO
 */
export async function consumeFromBatchLayers(
  productId: string,
  quantity: number,
  consumptionType: 'chipchip_sale' | 'b2b_delivery' | 'damage' | 'adjustment' | 'other',
  referenceId?: string,
  referenceDate?: string,
  notes?: string
): Promise<{
  batch_layer_id: string;
  quantity_consumed: number;
  unit_cost: number;
  total_cost: number;
}[]> {
  const { data: userData } = await supabase.auth.getUser();
  
  const { data, error } = await supabase.rpc('consume_from_batch_layers', {
    p_product_id: productId,
    p_quantity: quantity,
    p_consumption_type: consumptionType,
    p_reference_id: referenceId || null,
    p_reference_date: referenceDate || new Date().toISOString().split('T')[0],
    p_notes: notes || null,
    p_created_by: userData?.user?.id || null
  });
  
  if (error) throw error;
  return data || [];
}

/**
 * Get fallback unit cost when no batch layers exist.
 * Tries: 1) Last known inbound transaction cost, 2) Default of 0
 */
export async function getFallbackUnitCost(productId: string): Promise<{
  unitCost: number;
  source: 'last_transaction' | 'none';
}> {
  // Try to get last inbound transaction with unit_cost
  const { data: lastTx, error: txError } = await supabase
    .from('inventory_transactions')
    .select('unit_cost')
    .eq('product_id', productId)
    .eq('transaction_type', 'inbound')
    .not('unit_cost', 'is', null)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  
  if (!txError && lastTx?.unit_cost) {
    return { unitCost: lastTx.unit_cost, source: 'last_transaction' };
  }
  
  return { unitCost: 0, source: 'none' };
}

/**
 * Consume from batch layers with fallback cost logic.
 * If no batches exist, uses fallback cost and logs accordingly.
 */
export async function consumeFromBatchLayersWithFallback(
  productId: string,
  quantity: number,
  consumptionType: 'chipchip_sale' | 'b2b_delivery' | 'damage' | 'adjustment' | 'other',
  referenceId?: string,
  referenceDate?: string,
  notes?: string
): Promise<{
  consumed: {
    batch_layer_id: string;
    quantity_consumed: number;
    unit_cost: number;
    total_cost: number;
  }[];
  totalCost: number;
  avgUnitCost: number;
  usedFallback: boolean;
  fallbackSource?: 'last_transaction' | 'none';
}> {
  // First try normal batch consumption
  const consumed = await consumeFromBatchLayers(
    productId,
    quantity,
    consumptionType,
    referenceId,
    referenceDate,
    notes
  );
  
  // If we got consumption results, calculate totals
  if (consumed && consumed.length > 0) {
    const totalCost = consumed.reduce((sum, c) => sum + (c.total_cost || 0), 0);
    const avgUnitCost = quantity > 0 ? totalCost / quantity : 0;
    return { consumed, totalCost, avgUnitCost, usedFallback: false };
  }
  
  // No batch layers available - use fallback cost
  console.warn(`[FALLBACK] No batch layers for product ${productId}, using fallback cost`);
  
  const { unitCost, source } = await getFallbackUnitCost(productId);
  const totalCost = unitCost * quantity;
  
  console.log(`[FALLBACK] Using ${source} cost of ${unitCost}/kg for ${quantity}kg (total: ${totalCost})`);
  
  return {
    consumed: [],
    totalCost,
    avgUnitCost: unitCost,
    usedFallback: true,
    fallbackSource: source
  };
}

/**
 * Calculate damage value using FIFO without consuming, with fallback
 */
export async function calculateDamageValueWithFallback(
  productId: string,
  quantity: number
): Promise<{ value: number; usedFallback: boolean; fallbackSource?: string }> {
  const { data, error } = await supabase.rpc('calculate_damage_value', {
    p_product_id: productId,
    p_quantity: quantity
  });
  
  // If we got a positive value, use it
  if (!error && data && data > 0) {
    return { value: data, usedFallback: false };
  }
  
  // Fallback: get last known cost
  const { unitCost, source } = await getFallbackUnitCost(productId);
  const fallbackValue = unitCost * quantity;
  
  if (unitCost > 0) {
    console.log(`[FALLBACK] Damage value calculated using ${source}: ${fallbackValue}`);
  }
  
  return { value: fallbackValue, usedFallback: true, fallbackSource: source };
}

/**
 * Calculate damage value using FIFO without consuming
 */
export async function calculateDamageValue(
  productId: string,
  quantity: number
): Promise<number> {
  const { data, error } = await supabase.rpc('calculate_damage_value', {
    p_product_id: productId,
    p_quantity: quantity
  });
  
  if (error) throw error;
  return data || 0;
}

// ==================== Daily Stock Counts ====================

export async function getDailyStockCounts(filters?: {
  productId?: string;
  startDate?: string;
  endDate?: string;
}): Promise<DailyStockCount[]> {
  let query = supabase
    .from('daily_stock_counts')
    .select(`
      *,
      product:products(id, name, unit_of_measure)
    `)
    .order('count_date', { ascending: false });

  if (filters?.productId) {
    query = query.eq('product_id', filters.productId);
  }

  if (filters?.startDate) {
    query = query.gte('count_date', filters.startDate);
  }

  if (filters?.endDate) {
    query = query.lte('count_date', filters.endDate);
  }

  const { data, error } = await query;
  
  if (error) throw error;
  return data as unknown as DailyStockCount[];
}

export async function recordStockCount(
  input: CreateDailyStockCountInput
): Promise<DailyStockCount> {
  const { data: userData } = await supabase.auth.getUser();
  
  const { data, error } = await supabase
    .from('daily_stock_counts')
    .upsert({
      product_id: input.product_id,
      count_date: input.count_date,
      system_quantity: input.system_quantity,
      physical_quantity: input.physical_quantity,
      variance_reason: input.variance_reason || null,
      counted_by: userData?.user?.id || null
    }, {
      onConflict: 'product_id,count_date'
    })
    .select()
    .single();
  
  if (error) throw error;
  
  // If there's a variance, create an adjustment transaction
  const variance = input.physical_quantity - input.system_quantity;
  if (variance !== 0) {
    await recordInventoryTransaction({
      product_id: input.product_id,
      transaction_type: 'adjustment',
      quantity: variance,
      reference_type: 'manual',
      reference_number: `COUNT-${input.count_date}`,
      notes: input.variance_reason || `Stock count adjustment: ${variance > 0 ? '+' : ''}${variance}`
    });
  }
  
  return data as unknown as DailyStockCount;
}

// Record multiple stock counts in bulk
export async function recordBulkStockCounts(
  counts: CreateDailyStockCountInput[]
): Promise<DailyStockCount[]> {
  const results: DailyStockCount[] = [];
  
  for (const count of counts) {
    const result = await recordStockCount(count);
    results.push(result);
  }
  
  return results;
}

// ==================== Daily Damages ====================

export async function getDailyDamages(filters?: {
  productId?: string;
  startDate?: string;
  endDate?: string;
  damageType?: string;
}): Promise<DailyDamage[]> {
  let query = supabase
    .from('daily_damages')
    .select(`
      *,
      product:products(id, name, unit_of_measure)
    `)
    .order('damage_date', { ascending: false });

  if (filters?.productId) {
    query = query.eq('product_id', filters.productId);
  }

  if (filters?.startDate) {
    query = query.gte('damage_date', filters.startDate);
  }

  if (filters?.endDate) {
    query = query.lte('damage_date', filters.endDate);
  }

  if (filters?.damageType) {
    query = query.eq('damage_type', filters.damageType);
  }

  const { data, error } = await query;
  
  if (error) throw error;
  return data as unknown as DailyDamage[];
}

export async function recordDamage(
  input: CreateDailyDamageInput
): Promise<DailyDamage> {
  const { data: userData } = await supabase.auth.getUser();
  
  // Calculate damage value using FIFO with fallback
  let damageValue = 0;
  let usedFallback = false;
  try {
    const result = await calculateDamageValueWithFallback(input.product_id, input.quantity);
    damageValue = result.value;
    usedFallback = result.usedFallback;
    if (usedFallback && result.fallbackSource) {
      console.log(`[DAMAGE] Using fallback cost (${result.fallbackSource}) for damage value: ${damageValue}`);
    }
  } catch (e) {
    console.warn('[DAMAGE] Could not calculate damage value:', e);
  }
  
  // First, record the damage entry with damage_value
  const { data, error } = await supabase
    .from('daily_damages')
    .insert({
      product_id: input.product_id,
      damage_date: input.damage_date,
      quantity: input.quantity,
      damage_type: input.damage_type,
      reason: input.reason || null,
      recorded_by: userData?.user?.id || null,
      damage_value: damageValue
    })
    .select()
    .single();
  
  if (error) throw error;
  
  // Then, create a damage transaction to deduct from stock
  const { data: txData } = await supabase.rpc('record_inventory_transaction', {
    p_product_id: input.product_id,
    p_transaction_type: 'damage',
    p_quantity: input.quantity,
    p_unit_cost: input.quantity > 0 ? damageValue / input.quantity : null,
    p_reference_type: 'damage',
    p_reference_id: data.id,
    p_reference_number: `DMG-${input.damage_date}`,
    p_notes: `${input.damage_type}: ${input.reason || 'No reason provided'}`,
    p_created_by: userData?.user?.id || null
  });
  
  // Consume from batch layers using FIFO
  try {
    const { data: consumptionResult, error: consumeError } = await supabase
      .rpc('consume_from_batch_layers', {
        p_product_id: input.product_id,
        p_quantity: input.quantity,
        p_consumption_type: 'damage',
        p_reference_id: data.id,
        p_reference_date: input.damage_date,
        p_notes: `${input.damage_type}: ${input.reason || 'No reason'}`,
        p_created_by: userData?.user?.id || null
      });
    
    if (consumeError) {
      console.warn('[DAMAGE] Batch consumption warning:', consumeError.message);
    } else if (consumptionResult && consumptionResult.length > 0) {
      console.log(`[DAMAGE] Consumed ${input.quantity}kg from ${consumptionResult.length} batch(es), value: ${damageValue.toFixed(2)}`);
    }
  } catch (e) {
    console.warn('[DAMAGE] Could not consume from batch layers:', e);
  }
  
  return data as unknown as DailyDamage;
}

// Record multiple damages in bulk
export async function recordBulkDamages(
  damages: CreateDailyDamageInput[]
): Promise<DailyDamage[]> {
  const results: DailyDamage[] = [];
  
  for (const damage of damages) {
    const result = await recordDamage(damage);
    results.push(result);
  }
  
  return results;
}

// ==================== FIFO COGS Calculation ====================

export async function calculateFifoCogs(
  productId: string,
  quantity: number,
  asOfDate?: string
): Promise<{
  total_cogs: number;
  avg_unit_cost: number;
  layers_used: Array<{
    transaction_id: string;
    quantity_used: number;
    unit_cost: number;
  }>;
}> {
  const { data, error } = await supabase.rpc('calculate_fifo_cogs', {
    p_product_id: productId,
    p_quantity: quantity,
    p_as_of_date: asOfDate || new Date().toISOString()
  });
  
  if (error) throw error;
  
  const result = data?.[0];
  if (!result) {
    return { total_cogs: 0, avg_unit_cost: 0, layers_used: [] };
  }
  
  return {
    total_cogs: result.total_cogs || 0,
    avg_unit_cost: result.avg_unit_cost || 0,
    layers_used: (result.layers_used as Array<{
      transaction_id: string;
      quantity_used: number;
      unit_cost: number;
    }>) || []
  };
}
